import { z } from 'zod';

export const ROLES = [
  'admin',
  'building_manager',
  'maintenance',
  'case_manager',
  'vendor',
  'auditor',
] as const;

export type Role = (typeof ROLES)[number];

export const roleSchema = z.enum(ROLES);

/**
 * Numeric levels are used for "at-or-above" role checks. They are not a
 * formal hierarchy — admin can do anything, and gating logic in the API
 * should still enumerate the specific roles allowed per endpoint rather
 * than rely on level comparisons. The levels exist for sorting and for
 * the admin/non-admin distinction.
 */
export const ROLE_LEVELS: Record<Role, number> = {
  admin: 100,
  building_manager: 80,
  maintenance: 50,
  case_manager: 50,
  auditor: 20,
  vendor: 10,
};

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  building_manager: 'Building manager',
  maintenance: 'Maintenance staff',
  case_manager: 'Case manager',
  vendor: 'Vendor',
  auditor: 'Auditor (read-only)',
};

/** Roles that may be assigned through the org's user-management UI. Vendors
 *  go through the vendor-invite flow, not the staff invite flow. */
export const STAFF_ROLES = ROLES.filter((r) => r !== 'vendor') as readonly Exclude<Role, 'vendor'>[];

export const staffRoleSchema = z.enum(['admin', 'building_manager', 'maintenance', 'case_manager', 'auditor']);
